import type { Node as PMNode } from "@tiptap/pm/model";

export interface BlockContentRange {
  from: number;
  to: number;
}

export interface BlockSelectionRange {
  from: number;
  to: number;
}

export interface BlockRelativeSelectionRange {
  version: 1;
  from: number;
  to: number;
  mode: "blockRelative";
}

export interface InlineAtomRange {
  from: number;
  to: number;
}

function rangesOverlap(a: BlockSelectionRange, b: BlockSelectionRange): boolean {
  return a.from < b.to && a.to > b.from;
}

function clampRangeToBlock(
  range: BlockSelectionRange,
  blockContentRange: BlockContentRange
): BlockSelectionRange | null {
  const from = Math.max(blockContentRange.from, range.from);
  const to = Math.min(blockContentRange.to, range.to);
  if (to <= from) return null;
  return { from, to };
}

export function findInlineAtomRangesInRange(
  doc: PMNode,
  range: BlockSelectionRange
): InlineAtomRange[] {
  if (range.to <= range.from) return [];

  const ranges: InlineAtomRange[] = [];
  const from = Math.max(0, range.from - 1);
  const to = Math.min(doc.content.size, range.to + 1);

  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText && node.isInline && node.isAtom) {
      const atomRange = { from: pos, to: pos + node.nodeSize };
      if (rangesOverlap(atomRange, range)) {
        ranges.push(atomRange);
      }
      return false;
    }

    return true;
  });

  return ranges.sort((a, b) => a.from - b.from || a.to - b.to);
}

export function normalizeRangeToOpaqueAtoms(
  doc: PMNode,
  range: BlockSelectionRange
): BlockSelectionRange {
  if (range.to <= range.from) return range;

  let from = range.from;
  let to = range.to;
  for (const atomRange of findInlineAtomRangesInRange(doc, range)) {
    from = Math.min(from, atomRange.from);
    to = Math.max(to, atomRange.to);
  }

  return { from, to };
}

export function toBlockRelativeRange(
  doc: PMNode,
  blockContentRange: BlockContentRange,
  absoluteRange: BlockSelectionRange
): BlockRelativeSelectionRange | null {
  const clamped = clampRangeToBlock(absoluteRange, blockContentRange);
  if (!clamped) return null;

  const normalized = clampRangeToBlock(
    normalizeRangeToOpaqueAtoms(doc, clamped),
    blockContentRange
  );
  if (!normalized) return null;

  return {
    version: 1,
    from: normalized.from - blockContentRange.from,
    to: normalized.to - blockContentRange.from,
    mode: "blockRelative",
  };
}

export function isBlockRelativeSelectionRange(
  value: unknown
): value is BlockRelativeSelectionRange {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<BlockRelativeSelectionRange>;
  return (
    candidate.version === 1 &&
    candidate.mode === "blockRelative" &&
    Number.isSafeInteger(candidate.from) &&
    Number.isSafeInteger(candidate.to) &&
    (candidate.from ?? -1) >= 0 &&
    (candidate.to ?? -1) > (candidate.from ?? 0)
  );
}

export function fromBlockRelativeRange(
  doc: PMNode,
  blockContentRange: BlockContentRange,
  relativeRange: BlockRelativeSelectionRange
): BlockSelectionRange | null {
  if (!isBlockRelativeSelectionRange(relativeRange)) return null;

  const absoluteRange = {
    from: blockContentRange.from + relativeRange.from,
    to: blockContentRange.from + relativeRange.to,
  };
  const clamped = clampRangeToBlock(absoluteRange, blockContentRange);
  if (!clamped) return null;

  return clampRangeToBlock(normalizeRangeToOpaqueAtoms(doc, clamped), blockContentRange);
}
